import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../../state/auth.js';
import Card from './Card.jsx';
import Button from './Button.jsx';

// On your own profile: twee-staps-inloggen with an authenticator app. Switching on shows a
// QR code, a code confirms it and ten recovery codes are shown once. Switching off and new
// recovery codes need the password and a code.

const ERRORS = {
  invalid_mfa_code: 'Die code klopt niet (of is al gebruikt). Probeer de volgende code uit de app.',
  wrong_password: 'Je wachtwoord klopt niet.',
  account_locked: 'Te vaak een verkeerde code of wachtwoord. Probeer het over 15 minuten opnieuw.',
  mfa_already_enabled: 'Twee-staps-inloggen staat al aan.',
  mfa_setup_required: 'Begin opnieuw met aanzetten.',
};
const err = e => ERRORS[e?.code] || 'Dat lukte niet. Probeer het opnieuw.';

function RecoveryCodes({ codes, onDone }) {
  const [copied, setCopied] = useState(false);
  const text = codes.join('\n');
  async function copy() {
    try { await navigator.clipboard.writeText(text); setCopied(true); } catch { setCopied(false); }
  }
  return (
    <div style="display:flex;flex-direction:column;gap:10px">
      <div class="text-sm"><strong>Bewaar deze herstelcodes</strong> op een veilige plek (niet op je telefoon). Ben je je telefoon kwijt, dan log je met één van deze codes in. Elke code werkt één keer; je ziet ze nu voor het laatst.</div>
      <pre style="font-family:ui-monospace,Menlo,monospace;font-size:14px;line-height:1.7;background:var(--color-bg-secondary,#f9fafb);border:1px solid var(--color-border);border-radius:8px;padding:10px 14px;margin:0;columns:2;user-select:all">{text}</pre>
      <div style="display:flex;gap:8px;flex-wrap:wrap">
        <Button size="sm" variant="secondary" onClick={copy}>{copied ? '✓ Gekopieerd' : 'Kopiëren'}</Button>
        <Button size="sm" onClick={onDone}>Ik heb ze bewaard</Button>
      </div>
    </div>
  );
}

export default function MfaCard() {
  const [status, setStatus] = useState(null);
  const [mode, setMode] = useState(null);       // null | 'setup' | 'disable' | 'codes'
  const [setup, setSetup] = useState(null);     // { qrCode, secret }
  const [codes, setCodes] = useState(null);     // recovery codes to show once
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = () => api('/api/auth/mfa/status').then(setStatus).catch(() => setStatus(null));
  useEffect(() => { load(); }, []);
  const reset = () => { setMode(null); setSetup(null); setCode(''); setPassword(''); setError(null); };

  async function run(fn) {
    setBusy(true); setError(null);
    try { await fn(); } catch (e) { setError(err(e)); }
    setBusy(false);
  }
  const start = () => run(async () => { setSetup(await api('/api/auth/mfa/setup', { method: 'POST', body: '{}' })); setMode('setup'); });
  const confirm = e => { e.preventDefault(); run(async () => { const r = await api('/api/auth/mfa/enable', { method: 'POST', body: JSON.stringify({ code }) }); reset(); setCodes(r.recovery_codes); load(); }); };
  const off = e => { e.preventDefault(); run(async () => { await api('/api/auth/mfa/disable', { method: 'POST', body: JSON.stringify({ password, code }) }); reset(); load(); }); };
  const newCodes = e => { e.preventDefault(); run(async () => { const r = await api('/api/auth/mfa/recovery-codes', { method: 'POST', body: JSON.stringify({ password, code }) }); reset(); setCodes(r.recovery_codes); load(); }); };

  if (!status) return null;
  const codeInput = (
    <div class="form-group" style="margin:0">
      <label class="form-label" for="mfa-code">{mode === 'setup' ? 'Code uit de app' : 'Code uit de app of een herstelcode'}</label>
      <input id="mfa-code" class="form-input" inputMode={mode === 'setup' ? 'numeric' : 'text'} autoComplete="one-time-code" maxLength={mode === 'setup' ? 6 : 9}
        value={code} onInput={e => setCode(e.target.value)} placeholder={mode === 'setup' ? '123456' : '123456 of abcd-efgh'} required />
    </div>
  );
  const passwordInput = (
    <div class="form-group" style="margin:0">
      <label class="form-label" for="mfa-password">Wachtwoord</label>
      <input id="mfa-password" class="form-input" type="password" autoComplete="current-password" value={password} onInput={e => setPassword(e.target.value)} required />
    </div>
  );

  return (
    <Card title="🛡️ Twee-staps-inloggen">
      <div style="display:flex;flex-direction:column;gap:12px;max-width:420px">
        {codes ? (
          <RecoveryCodes codes={codes} onDone={() => setCodes(null)} />
        ) : mode === 'setup' && setup ? (
          <form onSubmit={confirm} style="display:flex;flex-direction:column;gap:10px">
            <div class="text-sm">1. Open een authenticator-app (bijv. Microsoft Authenticator, Google Authenticator of 1Password) en scan deze code.</div>
            <img src={setup.qrCode} alt="QR-code voor je authenticator-app" width="180" height="180" style="background:#fff;border-radius:8px;border:1px solid var(--color-border)" />
            <div class="text-sm text-muted">Scannen lukt niet? Vul deze sleutel in: <code style="user-select:all;overflow-wrap:anywhere">{setup.secret}</code></div>
            <div class="text-sm">2. Vul de code van 6 cijfers in die de app nu toont.</div>
            {codeInput}
            {error && <div class="alert alert-error" style="margin:0">{error}</div>}
            <div style="display:flex;gap:8px">
              <Button type="submit" size="sm" disabled={busy || code.length < 6}>{busy ? 'Bezig…' : 'Aanzetten'}</Button>
              <Button type="button" size="sm" variant="secondary" onClick={reset} disabled={busy}>Annuleren</Button>
            </div>
          </form>
        ) : mode === 'disable' || mode === 'codes' ? (
          <form onSubmit={mode === 'disable' ? off : newCodes} style="display:flex;flex-direction:column;gap:10px">
            <div class="text-sm">{mode === 'disable' ? 'Bevestig met je wachtwoord en een code om twee-staps-inloggen uit te zetten.' : 'Bevestig met je wachtwoord en een code. Je oude herstelcodes werken daarna niet meer.'}</div>
            {passwordInput}
            {codeInput}
            {error && <div class="alert alert-error" style="margin:0">{error}</div>}
            <div style="display:flex;gap:8px">
              <Button type="submit" size="sm" variant={mode === 'disable' ? 'danger' : 'primary'} disabled={busy}>{busy ? 'Bezig…' : mode === 'disable' ? 'Uitzetten' : 'Nieuwe codes maken'}</Button>
              <Button type="button" size="sm" variant="secondary" onClick={reset} disabled={busy}>Annuleren</Button>
            </div>
          </form>
        ) : status.enabled ? (
          <>
            <div class="text-sm"><span style="color:var(--color-success,#16a34a);font-weight:600">✓ Staat aan.</span> Bij het inloggen vraagt de app om een code uit je authenticator-app.</div>
            <div class="text-sm text-muted">Nog {status.recovery_codes_left} herstelcode{status.recovery_codes_left === 1 ? '' : 's'} over.{status.recovery_codes_left <= 3 ? ' Maak nieuwe aan voordat ze op zijn.' : ''}</div>
            <div style="display:flex;gap:8px;flex-wrap:wrap">
              <Button size="sm" variant="secondary" onClick={() => setMode('codes')}>Nieuwe herstelcodes</Button>
              <Button size="sm" variant="secondary" onClick={() => setMode('disable')}>Uitzetten</Button>
            </div>
          </>
        ) : (
          <>
            <div class="text-sm text-muted">Extra beveiliging: naast je wachtwoord vraagt de app bij het inloggen om een code uit een authenticator-app op je telefoon.</div>
            {error && <div class="alert alert-error" style="margin:0">{error}</div>}
            <div><Button size="sm" onClick={start} disabled={busy}>{busy ? 'Bezig…' : 'Aanzetten'}</Button></div>
          </>
        )}
      </div>
    </Card>
  );
}
